
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Popup from "../components/PopUP";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import TableFooter from "../components/TableFooter";
import DeleteModal from "../components/DeleteModal";
import api from "../Api";
import { IMAGE_URL, API_BASE_URL } from "../utils/constants";

const MANUFACTURER_LIST_URL =
  `${API_BASE_URL}/manufacturer/manufacturer-details/`;
const MANUFACTURER_UPLOAD_URL =
  `${API_BASE_URL}/manufacturer/upload-manufacturer-file/`;
const MANUFACTURER_STATUS_URL =
  `${API_BASE_URL}/manufacturer/upload-manufacturer-status/`;
const MANUFACTURER_BULK_DELETE_URL =
  `${API_BASE_URL}/manufacturer/bulk-delete-manufacturer/`;
const MANUFACTURER_APPROVE_URL =
  `${API_BASE_URL}/manufacturer/bulk-approve-manufacturer/`;
const MANUFACTURER_DISAPPROVE_URL =
  `${API_BASE_URL}/manufacturer/bulk-disapprove-manufacturer/`;
const MANUFACTURER_DELETE_URL =
  `${API_BASE_URL}/manufacturer/delete-manufacturer/`;

const RECORDS_PER_PAGE = 15;

export default function UploadManufacturerDetails() {
  const [manufacturers, setManufacturers] = useState([]);
  const [file, setFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const [popupMessage, setPopupMessage] = useState("");
  const [apiError, setApiError] = useState("");

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState("");

  const [hasUploaded, setHasUploaded] = useState(false);

  const [uploadTaskId, setUploadTaskId] = useState(null);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [updatedCount, setUpdatedCount] = useState(0);
  const [listLoading, setListLoading] = useState(false);

  const [pagination, setPagination] = useState({
    count: 0,
    total_pages: 0,
    next: null,
    previous: null,
  });

  const recordsPerPage = 15;

  const fetchManufacturers = useCallback(
    async (page = currentPage) => {
      try {
        setListLoading(true);

        const response = await api.get(
          `${MANUFACTURER_LIST_URL}?page=${page}&q=${encodeURIComponent(query)}`,
          { withCredentials: true }
        );

        const data = response.data;

        const records = Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
              ? data.data
              : [];

        setManufacturers(records);

        // Use pagination metadata returned by the backend.
        const totalPages =
          data?.total_pages ??
          Math.ceil((data?.count ?? records.length) / RECORDS_PER_PAGE);

        setPagination({
          count: data?.count ?? records.length,
          total_pages: totalPages,
          next: data?.next ?? null,
          previous: data?.previous ?? null,
        });

        setApiError("");

        return records;
      } catch (error) {
        console.error("Manufacturer API Error:", error);

        // setApiError(
        //   error.response?.data?.detail ||
        //     "Failed to fetch manufacturer details."
        // );

        return [];
      } finally {
        setListLoading(false);
      }
    },
    [currentPage, query]
  );


  useEffect(() => {
    const timer = setTimeout(() => {
      fetchManufacturers(currentPage);
    }, 400);

    return () => clearTimeout(timer);
  }, [fetchManufacturers, currentPage, query]);


  // KEEP CURRENT PAGE VALID AFTER DELETIONS OR DATA CHANGES
  useEffect(() => {
    if (
      pagination.total_pages > 0 &&
      currentPage > pagination.total_pages
    ) {
      setCurrentPage(pagination.total_pages);
    }
  }, [pagination.total_pages, currentPage]);

  // SEARCH CURRENTLY LOADED BACKEND PAGE
  const filteredManufacturers = useMemo(() => {
    const searchText = query.trim().toLowerCase();

    if (!searchText) return manufacturers;

    return manufacturers.filter((manufacturer) =>
      [
        manufacturer.company_name,
        manufacturer.headquarter,
        manufacturer.licence_type,
        manufacturer.author_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(searchText)
    );
  }, [manufacturers, query]);

  // BACKEND ALREADY RETURNS THE CURRENT PAGE.
  // DO NOT APPLY FRONTEND SLICE PAGINATION.
  const paginatedManufacturers = filteredManufacturers;

  // RESET TO PAGE 1 WHEN SEARCH CHANGES
  useEffect(() => {
    setCurrentPage(1);
  }, [query]);

  // SELECT / DESELECT SINGLE RECORD
  const handleSelectManufacturer = (id) => {
    setSelectedIds((previous) =>
      previous.some((selectedId) => String(selectedId) === String(id))
        ? previous.filter(
            (selectedId) => String(selectedId) !== String(id)
          )
        : [...previous, id]
    );
  };

  // SELECT / DESELECT VISIBLE RECORDS
  const visibleIds = paginatedManufacturers.map(
    (manufacturer) => manufacturer.id
  );

  const allVisibleSelected =
    visibleIds.length > 0 &&
    visibleIds.every((id) =>
      selectedIds.some(
        (selectedId) => String(selectedId) === String(id)
      )
    );

  const handleSelectAll = (checked) => {
    setSelectedIds((previous) =>
      checked
        ? [
            ...previous,
            ...visibleIds.filter(
              (id) =>
                !previous.some(
                  (selectedId) => String(selectedId) === String(id)
                )
            ),
          ]
        : previous.filter(
            (id) =>
              !visibleIds.some(
                (visibleId) => String(visibleId) === String(id)
              )
          )
    );
  };

  // CSV FILE SELECTION
  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;

    setFile(selectedFile);
    setApiError("");
    setPopupMessage("");

    if (
      selectedFile &&
      !selectedFile.name.toLowerCase().endsWith(".csv")
    ) {
      setApiError("Only CSV files are allowed.");
      setFile(null);
      event.target.value = "";
    }
  };

  // IMPORT STATUS POLLING
  const checkImportStatus = async (taskId) => {
    try {
      const response = await api.get(
        `${MANUFACTURER_STATUS_URL}${taskId}`,
        { withCredentials: true }
      );

      const data = response.data;

      if (data.status === "completed") {
        const inserted = data.inserted_records || 0;
        const updated = data.updated_records || 0;

        setUploadedCount(inserted);
        setUpdatedCount(updated);
        setHasUploaded(true);

        // Refresh the backend listing after import completes.
        setCurrentPage(1);
        await fetchManufacturers(1);

        setSelectedIds([]);
        setFile(null);
        setUploadTaskId(null);
        setLoading(false);

        const messages = [
          "Manufacturer import completed successfully.",
          `Created: ${inserted}`,
          `Updated: ${updated}`,
        ];

        if (data.message) {
          messages.push(data.message);
        }

        setPopupMessage(messages.join("\n"));

        const fileInput = document.getElementById(
          "manufacturer-upload-file"
        );

        if (fileInput) fileInput.value = "";

        return;
      }

      if (data.status === "failed") {
        setPopupMessage(
          data.message || "Failed to import manufacturer data."
        );

        setUploadTaskId(null);
        setLoading(false);
        return;
      }

      window.setTimeout(() => {
        checkImportStatus(taskId);
      }, 1500);
    } catch (error) {
      console.error(
        "Manufacturer Import Status Error:",
        error.response?.data || error
      );

      setApiError(
        error.response?.data?.detail ||
          "Failed to check manufacturer import status."
      );

      setUploadTaskId(null);
      setLoading(false);
    }
  };

  // CSV UPLOAD
  const handleSubmit = async (event) => {
    event.preventDefault();

    setApiError("");
    setPopupMessage("");

    if (!file) {
      setApiError("Please select a CSV file first.");
      return;
    }

    if (uploadTaskId) {
      setApiError("A manufacturer import is already processing.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        MANUFACTURER_UPLOAD_URL,
        formData,
        { withCredentials: true }
      );

      const data = response.data;

      // Handle APIs that return the result immediately.
      if (!data.task_id) {
        setUploadedCount(data.inserted_records || 0);
        setUpdatedCount(data.updated_records || 0);

        setHasUploaded(true);
        setPopupMessage(
          data.message ||
            data.detail ||
            "Manufacturer file uploaded successfully."
        );

        setFile(null);

        const fileInput = document.getElementById(
          "manufacturer-upload-file"
        );

        if (fileInput) fileInput.value = "";

        setCurrentPage(1);
        await fetchManufacturers(1);

        setLoading(false);
        return;
      }

      setUploadTaskId(data.task_id);
      setPopupMessage(
        data.message ||
          data.detail ||
          "Manufacturer import started. Please wait..."
      );

      setFile(null);

      const fileInput = document.getElementById(
        "manufacturer-upload-file"
      );

      if (fileInput) fileInput.value = "";

      await checkImportStatus(data.task_id);
    } catch (error) {
      console.error(
        "Manufacturer Upload Error:",
        error.response?.data || error
      );

      const detail = error.response?.data?.detail;

      const message =
        typeof detail === "string"
          ? detail
          : detail?.message ||
            error.response?.data?.message ||
            "Failed to upload manufacturer file.";

      setApiError(message);
      setLoading(false);
    }
  };

  // BULK APPROVE / DISAPPROVE
  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) {
      setPopupMessage("Please select at least one manufacturer.");
      return;
    }

    const endpoint =
      action === "approve"
        ? MANUFACTURER_APPROVE_URL
        : MANUFACTURER_DISAPPROVE_URL;

    const idsToUpdate = [...selectedIds];

    try {
      setBulkActionLoading(true);
      setApiError("");

      const response = await api.patch(
        endpoint,
        { manufacturer_ids: idsToUpdate },
        { withCredentials: true }
      );

      setManufacturers((previous) =>
        previous.map((manufacturer) =>
          idsToUpdate.some(
            (id) => String(id) === String(manufacturer.id)
          )
            ? {
                ...manufacturer,
                is_approved: action === "approve",
              }
            : manufacturer
        )
      );

      setSelectedIds([]);

      setPopupMessage(
        response.data?.message ||
          response.data?.detail ||
          `${idsToUpdate.length} manufacturer(s) ${
            action === "approve" ? "approved" : "disapproved"
          } successfully.`
      );
    } catch (error) {
      console.error(`Bulk ${action} Error:`, error);

      const detail = error.response?.data?.detail;

      setPopupMessage(
        typeof detail === "string"
          ? detail
          : error.response?.data?.message ||
              `Failed to ${action} selected manufacturers.`
      );
    } finally {
      setBulkActionLoading(false);
    }
  };

  // OPEN DELETE CONFIRMATION
  const handleDeleteClick = (manufacturer) => {
    setSelectedManufacturer(manufacturer);
    setShowModal(true);
  };

  const handleBulkDeleteClick = () => {
    if (selectedIds.length === 0) {
      setPopupMessage("Please select at least one manufacturer.");
      return;
    }

    setSelectedManufacturer(null);
    setShowModal(true);
  };

  // SINGLE DELETE AND BULK DELETE
  const handleConfirmDelete = async () => {
    const isSingleDelete = Boolean(selectedManufacturer?.id);

    const idsToDelete = isSingleDelete
      ? [selectedManufacturer.id]
      : [...selectedIds];

    if (idsToDelete.length === 0) return;

    try {
      setLoading(true);
      setApiError("");

      let response;

      if (isSingleDelete) {
        response = await api.delete(
          `${MANUFACTURER_DELETE_URL}?manufacturer_id=${encodeURIComponent(
            selectedManufacturer.id
          )}`,
          { withCredentials: true }
        );
      } else {
        response = await api.delete(
          MANUFACTURER_BULK_DELETE_URL,
          {
            data: { manufacturer_ids: idsToDelete },
            withCredentials: true,
          }
        );
      }

      setSelectedIds((previous) =>
        previous.filter(
          (id) =>
            !idsToDelete.some(
              (deletedId) => String(id) === String(deletedId)
            )
        )
      );

      setShowModal(false);
      setSelectedManufacturer(null);

      setManufacturers((previous) =>
      previous.filter(
        (manufacturer) =>
          !idsToDelete.some(
            (deletedId) =>
              String(deletedId) === String(manufacturer.id)
          )
      )
    );

    setPagination((previous) => ({
      ...previous,
      count: Math.max(
        0,
        previous.count -
          manufacturers.filter((manufacturer) =>
            idsToDelete.some(
              (deletedId) =>
                String(deletedId) === String(manufacturer.id)
            )
          ).length
      ),
    }));

      setPopupMessage(
        response.data?.message ||
          response.data?.detail ||
          (isSingleDelete
            ? "Manufacturer deleted successfully."
            : "Manufacturers deleted successfully.")
      );

      // await fetchManufacturers(currentPage);
    } catch (error) {
      console.error("Manufacturer Delete Error:", error);

      const detail = error.response?.data?.detail;

      setPopupMessage(
        typeof detail === "string"
          ? detail
          : error.response?.data?.message ||
              "Failed to delete manufacturer(s)."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-management-page">
      <Popup
        message={popupMessage}
        onClose={() => setPopupMessage("")}
      />

      <Sidebar />

      <div className="main-wrapper">
        <Header />

        <div className="content-wrapper">
          <section className="main-section spacer-y">
            <div className="container">
              <div className="row gy-4">
                <div className="col-xl-12">
                  <div className="common_main_bg_wrapper">
                    <div className="common_main_heading_wrapper">
                      <div className="vstack">
                        <nav className="breadcrumb_nav">
                          <ul className="breadcrumb mb-0" />
                        </nav>

                        <h1>Manufacturer Ingestion</h1>
                      </div>
                    </div>
                  </div>

                  {/* CSV UPLOAD - SAME UI */}
                  <form onSubmit={handleSubmit}>
                    <div className="row common-outer-card mx-2 my-5 p-3 justify-content-center">
                      <div className="col-12 col-md-6 col-lg-5">
                        <div className="form_field">
                          <label>Upload File here</label>

                          <div className="input_field">
                            <input
                              id="manufacturer-upload-file"
                              type="file"
                              className="form-control"
                              name="file"
                              accept=".csv,text/csv,application/vnd.ms-excel"
                              required
                              onChange={handleFileChange}
                              disabled={loading}
                            />

                            <span className="error">{apiError}</span>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-6 col-lg-3 mt-3">
                        <button
                          type="submit"
                          className="secondary_btn mt-3"
                          disabled={loading || !file}
                        >
                          {loading ? "Uploading..." : "Upload"}
                        </button>
                      </div>
                    </div>
                  </form>

                  {/* LISTING */}
                  {hasUploaded && (
                    <div className="common-table-wrapper mt-4">
                      
<div className="common-table-filter-wrapper">
  <div className="common-left-blk">
    <div className="common_sub_heading_wrapper mb-0">
      <h2>Uploaded Manufacturers Listing</h2>

      {/* <div className="d-flex flex-wrap gap-3 mt-2">
        <span>
          Total: <strong>{pagination.count}</strong>
        </span>
        <span>
          Created: <strong>{uploadedCount}</strong>
        </span>
        <span>
          Updated: <strong>{updatedCount}</strong>
        </span>
      </div> */}
    </div>
  </div>

  <div className="common-right-blk d-flex align-items-center gap-2">
    <div className="d-flex align-items-center gap-1 flex-shrink-0">
      <button
        type="button"
        className="secondary_btn"
        onClick={() => handleBulkAction("approve")}
        disabled={bulkActionLoading || selectedIds.length === 0}
      >
        {bulkActionLoading ? "Please wait..." : "Approve"}
      </button>

      <button
        type="button"
        className="secondary_btn"
        onClick={() => handleBulkAction("disapprove")}
        disabled={bulkActionLoading || selectedIds.length === 0}
      >
        Disapprove
      </button>

      <button
        type="button"
        className="secondary_btn"
        onClick={handleBulkDeleteClick}
        disabled={
          bulkActionLoading || loading || selectedIds.length === 0
        }
      >
        Delete
      </button>
    </div>

    <div className="common-sort-blk flex-grow-1">
      <div className="common-search-blk">
        <input
          type="search"
          className="form-control"
          id="manufacturer-search"
          placeholder="Search manufacturers ..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          name="manufacturer_search"
        />
      </div>
    </div>
  </div></div>

                      {loading || listLoading ? (
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ minHeight: "200px" }}
                        >
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">
                              Loading...
                            </span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="table-blk table-responsive">
                            <table className="table">
                              <thead>
                                <tr>
                                  <th>
                                    <input
                                      type="checkbox"
                                      aria-label="Select all manufacturers on this page"
                                      checked={allVisibleSelected}
                                      onChange={(event) =>
                                        handleSelectAll(event.target.checked)
                                      }
                                    />
                                  </th>

                                  <th>S.No.</th>
                                  <th>Manufacturer Name</th>
                                  <th>Logo</th>
                                  <th>Status</th>
                                  <th>Action</th>
                                </tr>
                              </thead>

                              <tbody>
                                {paginatedManufacturers.length > 0 ? (
                                  paginatedManufacturers.map(
                                    (manufacturer, index) => (
                                      <tr key={manufacturer.id}>
                                        <td>
                                          <input
                                            type="checkbox"
                                            aria-label={`Select ${
                                              manufacturer.company_name ||
                                              "manufacturer"
                                            }`}
                                            checked={selectedIds.some(
                                              (id) =>
                                                String(id) ===
                                                String(manufacturer.id)
                                            )}
                                            onChange={() =>
                                              handleSelectManufacturer(
                                                manufacturer.id
                                              )
                                            }
                                          />
                                        </td>

                                        <td>
                                          {(currentPage - 1) *
                                            recordsPerPage +
                                            index +
                                            1}
                                        </td>

                                        <td className="text-capitalize">
                                          {manufacturer.company_name || "-"}
                                        </td>

                                        <td>
                                          {manufacturer.logo ? (
                                            <img
                                              src={manufacturer.logo}
                                              alt={
                                                manufacturer.company_name ||
                                                "Manufacturer logo"
                                              }
                                              style={{
                                                width: "45px",
                                                height: "45px",
                                                objectFit: "contain",
                                              }}
                                            />
                                          ) : (
                                            "-"
                                          )}
                                        </td>

                                        <td>
                                          {manufacturer.is_approved ? (
                                            <span className="badge bg-light text-success">
                                              Active
                                            </span>
                                          ) : (
                                            <span className="badge bg-light text-danger">
                                              Inactive
                                            </span>
                                          )}
                                        </td>

                                        <td>
                                          <button
                                            type="button"
                                            className="border-0 bg-white"
                                            onClick={() =>
                                              handleDeleteClick(manufacturer)
                                            }
                                            aria-label={`Delete ${
                                              manufacturer.company_name ||
                                              "manufacturer"
                                            }`}
                                            disabled={loading}
                                          >
                                            <img
                                              src={IMAGE_URL + "trash-icon.svg"}
                                              alt="Trash"
                                            />
                                          </button>
                                        </td>
                                      </tr>
                                    )
                                  )
                                ) : (
                                  <tr>
                                    <td colSpan={6} className="text-center">
                                      {apiError ||
                                        "No manufacturer records found."}
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          <TableFooter
                            pagination={pagination}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                          />
                        </>
                      )}
                    </div>
                  )}

                  <DeleteModal
                    show={showModal}
                    onClose={() => {
                      setShowModal(false);
                      setSelectedManufacturer(null);
                    }}
                    onConfirm={handleConfirmDelete}
                    itemName={
                      selectedManufacturer
                        ? selectedManufacturer.company_name
                        : `${selectedIds.length} selected manufacturer(s)`
                    }
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
